import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tabs, Tab, Typography, Icon, type TabsProps } from "@mui/material";

import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import ParticipantPageLayout from "@/components/layouts/page/ParticipantPageLayout";
import AllSectionTabs from "../components/AllSectionTabs";
import { brandService } from "@/shared/services/brandService";
import { reportUserActivity } from "@/shared/helpers/userActivityHelper";
import NoDataFound from "@/components/NoDataFound";
import useFetcher from "@/helpers/hooks/useFetcher";
import PageLayout from "@/components/layouts/page/PageLayout";
import { settingSelector } from "@/shared/store/settingsSlice";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { getDashboardPath } from "@/helpers/utils";
import type {
  ResourceCenterApiResponse,
  ResourceCenterRequestParams,
  ResourcePaginationHandler,
  ResourceTabKey,
  ResourceTabLabel,
} from "../types";

const TAB_ORDER: ResourceTabKey[] = [
  "whats_new",
  "faqs",
  "helpful_articles",
  "educational_videos",
];

interface TabSectionProps {
  id: string;
  "aria-controls": string;
}

interface EducationCenterGlobalContext {
  selectedBrand?: {
    id?: number | string;
  };
  loggedInUserRole?: unknown;
  loggedInUser?: {
    id?: number | string;
  };
}

interface SettingsState {
  settings?: {
    brandSettings?: {
      has_brand_resource?: boolean;
    };
  };
}

interface SettingsSelectorValue {
  brandSettings?: {
    has_brand_resource?: boolean;
  };
}

interface ResourceFilters
  extends Omit<ResourceCenterRequestParams, "brandId"> {}

function tabSectionProps(index: number): TabSectionProps {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

export default function ResourceCenter() {
  const navigate = useNavigate();
  const { selectedBrand, loggedInUserRole, loggedInUser } = useContext(
    GlobalContext
  ) as EducationCenterGlobalContext;
  const settings = useSelector(settingSelector) as SettingsSelectorValue;
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;
  const [value, setValue] = useState(0);

  let tabs: ResourceTabLabel[];

  const [filters, setFilters] = useState<ResourceFilters>({
    pageIndex: 1,
    pageSize: 8,
    articlesPageIndex: 1,
    articlesPageSize: 8,
    newDataPageIndex: 1,
    newDataPageSize: 8,
  });

  const reqData = useMemo<ResourceCenterRequestParams>(() => {
    return {
      brandId: selectedBrand?.id,
      pageSize: filters?.pageSize,
      pageIndex: filters?.pageIndex,
      articlesPageIndex: filters?.articlesPageIndex,
      articlesPageSize: filters?.articlesPageSize,
      newDataPageIndex: filters?.newDataPageIndex,
      newDataPageSize: filters?.newDataPageSize,
    };
  }, [selectedBrand?.id, loggedInUser?.id, filters]);

  // handle functions
  const handlePageChange: ResourcePaginationHandler = (_event, value) => {
    setFilters({ ...filters, pageIndex: value });
  };

  const handleArticlePageChange: ResourcePaginationHandler = (
    _event,
    value
  ) => {
    setFilters({ ...filters, articlesPageIndex: value });
  };
  const handleWhatsNewPage: ResourcePaginationHandler = (_event, value) => {
    setFilters({ ...filters, newDataPageIndex: value });
  };

  if (!selectedBrand?.id) {
    navigate(`${getDashboardPath()}`, {
      replace: true,
    });
  }

  const hasBrandResources = useSelector(
    (state: SettingsState) => state.settings?.brandSettings?.has_brand_resource
  );
  useEffect(() => {
    if (!hasBrandResources) {
      navigate(`/dashboard`, { replace: true });
    }
  }, [hasBrandResources, navigate]);

  useEffect(() => {
    reportUserActivity("resource_center");
  }, [loggedInUserRole]);

  function modifyTabName(tab: ResourceTabKey): ResourceTabLabel {
    const tabMapping: Record<ResourceTabKey, ResourceTabLabel> = {
      faqs: "FAQ",
      educational_videos: "Educational Videos",
      helpful_articles: "Helpful Content",
      whats_new: "What's New",
    };

    return tabMapping[tab];
  }

  const { data, isLoading } = useFetcher(
    reqData,
    brandService.getResourceCenterData,
    { revalidateOnFocus: false }
  );
  const resourceData = data as ResourceCenterApiResponse | undefined;

  useEffect(() => {
    if (settings?.brandSettings?.has_brand_resource === false) {
      navigate(getDashboardPath(), { replace: true });
    }
  }, [settings?.brandSettings?.has_brand_resource, selectedBrand, navigate]);

  const checkTabs = resourceData
    ? TAB_ORDER.filter((k) => {
        if (k === "whats_new") {
          return resourceData[k][0].newAddedData.length > 0;
        } else if (k === "educational_videos") {
          return resourceData[k][0].videos.length > 0;
        } else if (k === "helpful_articles") {
          return resourceData[k][0].articles.length > 0;
        } else {
          return resourceData[k].length > 0;
        }
      }).map(modifyTabName)
    : [];
  tabs = checkTabs;

  const faqData = resourceData?.faqs || [];
  const educationalVideos = resourceData?.educational_videos[0]?.videos || [];
  const articles = resourceData?.helpful_articles[0]?.articles || [];
  const whatsNew = resourceData?.whats_new[0]?.newAddedData || [];
  const totalVideos =
    resourceData?.educational_videos[0]?.totalVideosCount || 0;
  const totalArticles =
    resourceData?.helpful_articles[0]?.totalArticlesCount || 0;
  const totalNewData = resourceData?.whats_new[0]?.totalCount || 0;

  const fetchResourceData = useCallback((data: Partial<ResourceFilters>) => {
    setFilters({
      pageIndex: 1,
      pageSize: 8,
      articlesPageIndex: 1,
      articlesPageSize: 8,
      newDataPageIndex: 1,
      newDataPageSize: 8,
      ...data,
    });
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      fetchResourceData({
        pageIndex: 1,
        pageSize: 8,
        articlesPageIndex: 1,
        articlesPageSize: 8,
        newDataPageIndex: 1,
        newDataPageSize: 8,
      });
    }
  }, [selectedBrand, fetchResourceData]);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  const handleActivity = (currentTab?: string) => {
    if (currentTab === "FAQ") {
      reportUserActivity("Brand_faq");
    } else if (currentTab === "Educational Videos") {
      reportUserActivity("Educational_video");
    } else if (currentTab === "Helpful Content") {
      reportUserActivity("Helpful_articles");
    } else if (currentTab === "What's New") {
      reportUserActivity("Whats_new");
    }
  };

  const handleChange: TabsProps["onChange"] = (event, newValue) => {
    setValue(newValue);
    const currentTab = (event.target as HTMLElement | null)?.textContent;
    handleActivity(currentTab);
  };

  const tabImages: Record<ResourceTabLabel, string> = {
    FAQ: "faq",
    "Educational Videos": "educational_videos",
    "Helpful Content": "helpful_content",
    "What's New": "whats_new",
  };

  function getImageSrcForTab(tab: ResourceTabLabel): string | undefined {
    // const tabIndex = tabs.indexOf(tab);
    if (tab === "FAQ") {
      return tabImages["FAQ"];
    } else if (tab === "Educational Videos") {
      return tabImages["Educational Videos"];
    } else if (tab === "Helpful Content") {
      return tabImages["Helpful Content"];
    } else if (tab === "What's New") {
      return tabImages["What's New"];
    }
  }

  useEffect(() => {
    if (tabs.length && !tabs[value]) {
      const firstTabWithData = tabs[0]; // First tab with data
      setValue(tabs.indexOf(firstTabWithData)); // Set to first tab with data
    }
  }, [tabs, value]);

  return (
    <>
      <PageLayout className="resources">
        <div className=" resource-center">
          <div className="row justify-content-center mb-5 mx-0">
            <div className="col-md-12 col-lg-12">
              <Typography
                component="h1"
                variant="h5"
                className="main-heading text-center my-4"
                gutterBottom
              >
                Education Center
              </Typography>
              <Typography
                component="div"
                className="text-center subheading"
                gutterBottom
              >
                {/* Helpful videos and content about your local advertising and the
                Hyperlocology platform. */}
                Learn about everything from customer success stories, product
                info, to viewpoints from the core team
              </Typography>
            </div>
          </div>
          <div className="container">
            <div className="container tabs-container">
              {!isLoading && tabs && tabs.length > 0 ? (
                <Tabs
                  value={value}
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  aria-label="scrollable auto tabs example"
                  className={tabs?.length === 1 ? "all-section" : ""}
                >
                  {tabs?.map((tab, i) => {
                    const imageSrc = getImageSrcForTab(tab);
                    return (
                      <Tab
                        label={
                          <div>
                            {/* <img
                              src={imageSrc}
                              alt={tab}
                              className={`tab-image ${value === i ? "selected-image" : ""
                                }`}
                            /> */}
                            <Icon
                              component={RpSvgIcon}
                              name={imageSrc}
                              color="primary"
                              fontSize="small"
                              className="mr-2"
                            />
                            {tab}
                          </div>
                        }
                        key={i}
                        {...tabSectionProps(i)}
                      />
                    );
                  })}
                </Tabs>
              ) : null}
            </div>
          </div>
        </div>
        <ParticipantPageLayout className="resource-section">
          <div className="sections">
            <div className="container">
              {tabs && tabs.length ? (
                <AllSectionTabs
                  tabs={tabs}
                  value={value}
                  faqData={faqData}
                  educationalVideos={educationalVideos}
                  totalVideos={totalVideos}
                  handlePageChange={handlePageChange}
                  pageIndex={filters?.pageIndex}
                  pageSize={filters?.pageSize}
                  articles={articles}
                  totalArticles={totalArticles}
                  articlesPageIndex={filters?.articlesPageIndex}
                  articlesPageSize={filters?.articlesPageSize}
                  handleArticlePageChange={handleArticlePageChange}
                  whatsNew={whatsNew}
                  newDataPageIndex={filters?.newDataPageIndex}
                  newDataPageSize={filters?.newDataPageSize}
                  totalNewData={totalNewData}
                  handleWhatsNewPage={handleWhatsNewPage}
                />
              ) : (
                <NoDataFound height={"50vh"} message="Loading..." />
              )}
            </div>
          </div>
        </ParticipantPageLayout>
      </PageLayout>
    </>
  );
}
